import React from 'react';
import { Ban, DollarSign, ShoppingBag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { orderLabel } from '../utils/itemName';

// `label` stays English here - it is the translation key, turned into Nepali by
// t() at render time.
const PAYMENT_BADGES = {
  cash: { label: 'Cash', className: 'bg-green-100 text-green-800' },
  bank: { label: 'Bank', className: 'bg-blue-100 text-blue-800' },
  due: { label: 'Due', className: 'bg-amber-100 text-amber-800' }
};

/**
 * Today's finished sales. Voided rows stay in the list, greyed out, so the
 * correction is visible rather than the sale simply disappearing.
 */
function CompletedSales({ orders, loading, total, onVoid, formatTime }) {
  const { t, language } = useLanguage();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Summary - matches the compact two-up tiles on the open tokens tab */}
      <div className="grid grid-cols-2 gap-2 sm:gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg flex-shrink-0">
              <ShoppingBag className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] sm:text-sm font-medium text-gray-500 truncate">{t('Sales Today')}</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight">
                {orders.filter(order => order.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg flex-shrink-0">
              <DollarSign className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] sm:text-sm font-medium text-gray-500 truncate">{t('Revenue')}</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight truncate">
                Rs {total.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <ShoppingBag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('No sales yet today')}</h3>
          <p className="text-gray-500">{t('Completed orders will appear here.')}</p>
        </div>
      ) : (
        <div className="space-y-2 sm:space-y-3">
          {orders.map((order) => {
            const isVoided = order.status === 'voided';
            const amount = order.finalAmount ?? order.totalAmount ?? 0;

            return (
              <div
                key={order._id}
                className={`bg-white rounded-xl shadow-sm border p-3 sm:p-5 ${
                  isVoided ? 'border-gray-200 opacity-60' : 'border-gray-200'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className={`text-base sm:text-lg font-semibold text-gray-900 ${isVoided ? 'line-through' : ''}`}>
                        {orderLabel(order, language)}
                        {order.quantity > 1 && <span className="text-cafe-600"> × {order.quantity}</span>}
                      </h3>
                      {isVoided ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                          {t('Voided')}
                        </span>
                      ) : (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          PAYMENT_BADGES[order.paymentType]?.className || 'bg-blue-100 text-blue-800'
                        }`}>
                          {t(PAYMENT_BADGES[order.paymentType]?.label || 'Bank')}
                          {order.paymentType === 'due' && order.dueStatus === 'settled' && ` · ${t('Paid')}`}
                        </span>
                      )}
                      <span className="text-xs text-gray-500">{formatTime(order.completedAt)}</span>
                    </div>

                    {/* Who owes it, so an unpaid bill can be chased straight
                        from the day's sales without opening the Dues page */}
                    {order.paymentType === 'due' && order.dueCustomerName && (
                      <p className="text-xs text-amber-700 mt-1">
                        {order.dueCustomerName}
                        {order.dueCustomerMobile ? ` · ${order.dueCustomerMobile}` : ''}
                      </p>
                    )}

                    {order.discountAmount > 0 && (
                      <p className="text-xs text-orange-600 mt-1">
                        {t('Discount: -Rs {amount}', { amount: order.discountAmount.toFixed(2) })}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between sm:justify-end gap-4 flex-shrink-0">
                    <span className={`text-lg font-bold ${isVoided ? 'text-gray-400 line-through' : 'text-cafe-600'}`}>
                      Rs {amount.toFixed(2)}
                    </span>

                    {!isVoided && (
                      <button
                        onClick={() => onVoid(order)}
                        className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center gap-2"
                        title={t('Void this sale')}
                      >
                        <Ban className="h-4 w-4" />
                        {t('Void')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CompletedSales;
